import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../api/client';

/**
 * History of RSVP uploads for an event, with a guarded "Reverse upload" action.
 *
 * Each row is one RsvpImportBatch. A Confirmed batch whose guests are still
 * present can be reversed — which deletes every guest + party (TicketOrder)
 * created by that upload, so the organizer can re-upload a corrected file.
 * Reversal is refused by the backend if any of the batch's guests have been
 * seated or checked in; we surface those blocking names in the confirm dialog.
 *
 * Mirrors the order-import UploadHistory, but RSVP batches have no snapshots —
 * reversal is a direct delete-by-batchId, so the UI is lighter.
 */

interface RsvpBatch {
  id: string;
  fileName: string | null;
  uploadedByName: string;
  partiesCreated: number;
  guestsCreated: number;
  skippedCount: number;
  status: 'Confirmed' | 'RolledBack';
  createdAt: string;
  rolledBackAt: string | null;
  liveGuestCount: number;
}

interface RollbackPreview {
  batchId: string;
  status: string;
  alreadyRolledBack: boolean;
  guestsToDelete: number;
  ordersToDelete: number;
  blockingGuestNames: string[];
  canReverse: boolean;
}

interface ReportGuest {
  fullName: string;
  chapterAffiliation: string | null;
  rsvpStatus: string;
  paymentStatus: string;
}
interface ReportParty {
  referenceId: string;
  purchaserName: string;
  purchaserEmail: string;
  chapterAffiliation: string | null;
  ticketCount: number;
  guests: ReportGuest[];
}
interface BatchReport {
  id: string;
  fileName: string | null;
  uploadedByName: string;
  status: 'Confirmed' | 'RolledBack';
  createdAt: string;
  rolledBackAt: string | null;
  partiesCreated: number;
  guestsCreated: number;
  skippedCount: number;
  liveParties: number;
  liveGuests: number;
  parties: ReportParty[];
}

const statusColors: Record<string, { bg: string; text: string }> = {
  Confirmed: { bg: '#dcfce7', text: '#166534' },
  RolledBack: { bg: '#ede9fe', text: '#5b21b6' },
};

export function RsvpImportHistory({
  eventId,
  refreshTrigger,
  onReversed,
}: {
  eventId: string;
  refreshTrigger: number;
  onReversed?: () => void;
}) {
  const [batches, setBatches] = useState<RsvpBatch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Reverse-confirm dialog state.
  const [confirmBatch, setConfirmBatch] = useState<RsvpBatch | null>(null);
  const [preview, setPreview] = useState<RollbackPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [reversing, setReversing] = useState(false);

  // Expandable per-batch detail (fetched lazily, cached by batch id).
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reports, setReports] = useState<Record<string, BatchReport>>({});
  const [reportLoadingId, setReportLoadingId] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiClient<RsvpBatch[]>(`/events/${eventId}/rsvp-imports`);
      setBatches(res || []);
    } catch {
      setBatches([]);
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, refreshTrigger]);

  async function openConfirm(batch: RsvpBatch) {
    setConfirmBatch(batch);
    setPreview(null);
    setError('');
    setPreviewLoading(true);
    try {
      const p = await apiClient<RollbackPreview>(
        `/events/rsvp-imports/${batch.id}/rollback-preview`,
      );
      setPreview(p);
    } catch (e: any) {
      setError(e?.message || 'Could not load reversal preview.');
    } finally {
      setPreviewLoading(false);
    }
  }

  function closeConfirm() {
    setConfirmBatch(null);
    setPreview(null);
    setError('');
  }

  async function doReverse() {
    if (!confirmBatch) return;
    setReversing(true);
    setError('');
    try {
      await apiClient(`/events/rsvp-imports/${confirmBatch.id}`, { method: 'DELETE' });
      closeConfirm();
      await fetchHistory();
      onReversed?.();
    } catch (e: any) {
      setError(e?.message || 'Reversal failed. You may not have permission for this action.');
    } finally {
      setReversing(false);
    }
  }

  async function loadReport(batchId: string): Promise<BatchReport | null> {
    if (reports[batchId]) return reports[batchId];
    setReportLoadingId(batchId);
    try {
      const r = await apiClient<BatchReport>(`/events/rsvp-imports/${batchId}/report`);
      setReports((prev) => ({ ...prev, [batchId]: r }));
      return r;
    } catch {
      return null;
    } finally {
      setReportLoadingId(null);
    }
  }

  async function toggleExpand(batchId: string) {
    if (expandedId === batchId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(batchId);
    await loadReport(batchId);
  }

  async function printReport(batch: RsvpBatch) {
    const report = (await loadReport(batch.id)) || null;
    if (!report) {
      setError('Could not load the report to print.');
      return;
    }
    const esc = (s: string) =>
      String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));

    const partyBlocks = report.parties
      .map((p) => {
        const guestRows = p.guests
          .map(
            (g) =>
              `<tr><td>${esc(g.fullName)}</td><td>${esc(g.chapterAffiliation || '—')}</td><td>${esc(g.rsvpStatus)}</td><td>${esc(g.paymentStatus)}</td></tr>`,
          )
          .join('');
        return `<h3>${esc(p.referenceId)}${p.purchaserName ? ` — ${esc(p.purchaserName)}` : ''} <span class="count">(${p.guests.length} guest${p.guests.length === 1 ? '' : 's'})</span></h3>
          <table><thead><tr><th>Guest</th><th>Affiliation</th><th>RSVP</th><th>Payment</th></tr></thead><tbody>${guestRows}</tbody></table>`;
      })
      .join('');

    const uploaded = new Date(report.createdAt).toLocaleString('en-US', {
      month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    });

    const html = `<!DOCTYPE html><html><head>
      <title>RSVP Import — ${esc(report.fileName || report.id)}</title>
      <style>
        body { font-family: sans-serif; padding: 20px; font-size: 12px; color: #222; }
        h1 { font-size: 18px; margin-bottom: 4px; }
        h3 { font-size: 13px; margin: 1.1rem 0 0.3rem 0; border-bottom: 1px solid #333; padding-bottom: 3px; }
        h3 .count { font-weight: 400; color: #666; font-size: 11px; }
        p { color: #666; font-size: 12px; margin: 2px 0 10px 0; }
        .summary { display: flex; gap: 2rem; margin: 10px 0 4px 0; }
        .summary .item { text-align: center; }
        .summary .value { font-size: 1.4rem; font-weight: 700; color: #1a6b3c; }
        .summary .label { font-size: 10px; color: #666; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
        th { text-align: left; padding: 5px 8px; border-bottom: 2px solid #333; font-size: 11px; text-transform: uppercase; color: #555; }
        td { padding: 4px 8px; border-bottom: 1px solid #ddd; font-size: 11px; }
        .status { font-size: 11px; color: #666; }
      </style>
    </head><body>
      <h1>RSVP Import Report</h1>
      <p><strong>File:</strong> ${esc(report.fileName || '—')} &nbsp;|&nbsp; <strong>By:</strong> ${esc(report.uploadedByName)} &nbsp;|&nbsp; <strong>Uploaded:</strong> ${uploaded}</p>
      <p class="status"><strong>Status:</strong> ${esc(report.status)}${report.status === 'RolledBack' && report.rolledBackAt ? ` (reversed ${new Date(report.rolledBackAt).toLocaleString()})` : ''} &nbsp;|&nbsp; Printed ${new Date().toLocaleDateString()}</p>
      <div class="summary">
        <div class="item"><div class="value">${report.partiesCreated}</div><div class="label">Parties</div></div>
        <div class="item"><div class="value">${report.guestsCreated}</div><div class="label">Guests</div></div>
        <div class="item"><div class="value">${report.skippedCount}</div><div class="label">Skipped</div></div>
      </div>
      ${report.status === 'RolledBack'
        ? '<p>This upload was reversed; its parties and guests are no longer in the system. Counts above are from import time.</p>'
        : partyBlocks || '<p>No parties found for this upload.</p>'}
    </body></html>`;

    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 300);
    }
  }

  function formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  function renderBadge(status: string) {
    const c = statusColors[status] || { bg: '#f3f4f6', text: '#4b5563' };
    return (
      <span style={{ ...styles.badge, backgroundColor: c.bg, color: c.text }}>{status}</span>
    );
  }

  if (!isLoading && batches.length === 0) {
    return null; // Nothing uploaded yet — keep the panel clean.
  }

  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>Upload history</h4>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>File</th>
              <th style={styles.th}>Uploaded</th>
              <th style={styles.th}>By</th>
              <th style={styles.thNum}>Parties</th>
              <th style={styles.thNum}>Guests</th>
              <th style={styles.th}>Status</th>
              <th style={{ ...styles.th, width: '120px' }}></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} style={styles.emptyRow}>Loading history…</td>
              </tr>
            ) : (
              batches.flatMap((b) => {
                const isExpanded = expandedId === b.id;
                const report = reports[b.id];
                const rows: React.ReactNode[] = [
                  <tr key={b.id}>
                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.caretBtn}
                        onClick={() => toggleExpand(b.id)}
                        aria-label={isExpanded ? `Collapse ${b.fileName || b.id}` : `View guests in ${b.fileName || b.id}`}
                        aria-expanded={isExpanded}
                        title="View parties and guests"
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                      {b.fileName || <span style={styles.dim}>—</span>}
                    </td>
                    <td style={styles.td}>{formatDate(b.createdAt)}</td>
                    <td style={styles.td}>{b.uploadedByName}</td>
                    <td style={styles.tdNum}>{b.partiesCreated}</td>
                    <td style={styles.tdNum}>
                      {b.guestsCreated}
                      {b.status === 'Confirmed' && b.liveGuestCount !== b.guestsCreated && (
                        <span style={styles.dim}> ({b.liveGuestCount} left)</span>
                      )}
                    </td>
                    <td style={styles.td}>{renderBadge(b.status)}</td>
                    <td style={styles.td}>
                      <div style={styles.actionCell}>
                        <button
                          type="button"
                          style={styles.iconBtn}
                          onClick={() => printReport(b)}
                          aria-label={`Print report for ${b.fileName || b.id}`}
                          title="Print this upload's report"
                        >
                          🖨️
                        </button>
                        {b.status === 'Confirmed' ? (
                          <button
                            type="button"
                            style={styles.reverseBtn}
                            onClick={() => openConfirm(b)}
                            aria-label={`Reverse upload ${b.fileName || b.id}`}
                            title="Reverse this upload (delete its guests and parties)"
                          >
                            ↩ Reverse
                          </button>
                        ) : (
                          <span style={styles.dim}>Reversed {formatDate(b.rolledBackAt)}</span>
                        )}
                      </div>
                    </td>
                  </tr>,
                ];

                if (isExpanded) {
                  rows.push(
                    <tr key={`${b.id}-detail`}>
                      <td colSpan={7} style={styles.detailCell}>
                        {reportLoadingId === b.id && !report ? (
                          <div style={styles.dim}>Loading guests…</div>
                        ) : b.status === 'RolledBack' ? (
                          <div style={styles.dim}>
                            This upload was reversed — its {b.guestsCreated} guest
                            {b.guestsCreated === 1 ? '' : 's'} and {b.partiesCreated} part
                            {b.partiesCreated === 1 ? 'y' : 'ies'} are no longer in the system.
                          </div>
                        ) : report && report.parties.length > 0 ? (
                          <div style={styles.detailWrap}>
                            {report.parties.map((p, i) => (
                              <div key={i} style={styles.partyBlock}>
                                <div style={styles.partyHead}>
                                  <span style={styles.partyRef}>{p.referenceId}</span>
                                  {p.purchaserName && <span style={styles.dim}> · {p.purchaserName}</span>}
                                  <span style={styles.dim}> · {p.guests.length} guest{p.guests.length === 1 ? '' : 's'}</span>
                                </div>
                                <ul style={styles.guestList}>
                                  {p.guests.map((g, j) => (
                                    <li key={j} style={styles.guestItem}>
                                      {g.fullName}
                                      {g.chapterAffiliation && <span style={styles.dim}> · {g.chapterAffiliation}</span>}
                                      <span style={styles.dim}> · {g.rsvpStatus}/{g.paymentStatus}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={styles.dim}>No parties found for this upload.</div>
                        )}
                      </td>
                    </tr>,
                  );
                }
                return rows;
              })
            )}
          </tbody>
        </table>
      </div>

      {confirmBatch && (
        <div style={styles.modalOverlay} onClick={closeConfirm}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Reverse this upload?</h3>
            <p style={styles.modalBody}>
              This permanently deletes the guests and parties created by{' '}
              <strong>{confirmBatch.fileName || 'this upload'}</strong>. You can then re-upload a
              corrected file.
            </p>

            {error && <div style={styles.error}>{error}</div>}

            {previewLoading ? (
              <div style={styles.modalBody}>Checking what would be removed…</div>
            ) : preview ? (
              <>
                <div style={styles.previewBox}>
                  <div>
                    <strong>{preview.guestsToDelete}</strong> guest
                    {preview.guestsToDelete === 1 ? '' : 's'} and{' '}
                    <strong>{preview.ordersToDelete}</strong> part
                    {preview.ordersToDelete === 1 ? 'y' : 'ies'} will be deleted.
                  </div>
                </div>

                {preview.blockingGuestNames.length > 0 && (
                  <div style={styles.blockBox}>
                    <strong>Cannot reverse yet.</strong> {preview.blockingGuestNames.length} guest
                    {preview.blockingGuestNames.length === 1 ? ' is' : 's are'} already seated or
                    checked in:
                    <div style={styles.blockNames}>
                      {preview.blockingGuestNames.slice(0, 10).join(', ')}
                      {preview.blockingGuestNames.length > 10 &&
                        ` +${preview.blockingGuestNames.length - 10} more`}
                    </div>
                    <div style={styles.blockHint}>
                      Unassign their seats / undo check-in first, then try again.
                    </div>
                  </div>
                )}
              </>
            ) : null}

            <div style={styles.modalActions}>
              <button style={styles.secondaryBtn} onClick={closeConfirm} disabled={reversing}>
                Cancel
              </button>
              <button
                style={{
                  ...styles.dangerBtn,
                  ...(!preview?.canReverse || reversing ? styles.dangerBtnDisabled : {}),
                }}
                onClick={doReverse}
                disabled={!preview?.canReverse || reversing}
              >
                {reversing ? 'Reversing…' : 'Reverse upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { marginTop: '1.5rem' },
  heading: { fontSize: '0.9rem', fontWeight: 600, color: '#374151', margin: '0 0 0.75rem 0' },
  tableWrapper: { overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' },
  th: { padding: '0.6rem 0.75rem', textAlign: 'left', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' },
  thNum: { padding: '0.6rem 0.75rem', textAlign: 'right', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' },
  td: { padding: '0.5rem 0.75rem', borderBottom: '1px solid #f3f4f6', color: '#4b5563', verticalAlign: 'top' },
  tdNum: { padding: '0.5rem 0.75rem', borderBottom: '1px solid #f3f4f6', color: '#4b5563', textAlign: 'right', fontWeight: 600 },
  dim: { color: '#9ca3af', fontWeight: 400 },
  badge: { display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 500 },
  emptyRow: { textAlign: 'center', padding: '2rem 1rem', color: '#6b7280', fontSize: '0.85rem' },
  reverseBtn: { background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0.3rem 0.6rem', fontSize: '0.78rem', fontWeight: 600, color: '#991b1b', cursor: 'pointer' },
  caretBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '0 6px 0 0', fontSize: '0.7rem', color: '#6b7280', lineHeight: 1 },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: '0.95rem', borderRadius: '4px', lineHeight: 1 },
  actionCell: { display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' as const },
  detailCell: { padding: '0.5rem 0.75rem 0.9rem 1.6rem', background: '#fafafa', borderBottom: '1px solid #f3f4f6' },
  detailWrap: { display: 'flex', flexDirection: 'column' as const, gap: '0.6rem' },
  partyBlock: { borderLeft: '3px solid #c8e6c9', paddingLeft: '0.6rem' },
  partyHead: { fontSize: '0.8rem', color: '#374151', marginBottom: '0.2rem' },
  partyRef: { fontFamily: 'monospace', fontWeight: 700, color: '#1565c0', fontSize: '0.78rem' },
  guestList: { margin: '0 0 0 0.9rem', padding: 0, fontSize: '0.78rem', color: '#4b5563', lineHeight: 1.5 },
  guestItem: { listStyle: 'disc' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modal: { background: '#fff', borderRadius: '12px', padding: '1.5rem', maxWidth: '480px', width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' },
  modalTitle: { margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: '#0f2d1f' },
  modalBody: { fontSize: '0.88rem', color: '#555', lineHeight: 1.5, margin: '0 0 1rem 0' },
  previewBox: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.85rem', color: '#991b1b', marginBottom: '0.75rem' },
  blockBox: { background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.82rem', color: '#92400e', marginBottom: '0.75rem' },
  blockNames: { marginTop: '0.4rem', fontWeight: 600 },
  blockHint: { marginTop: '0.4rem', fontStyle: 'italic', color: '#b45309' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1rem' },
  secondaryBtn: { background: '#e0e0e0', color: '#333', border: 'none', borderRadius: '6px', padding: '0.6rem 1.1rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' },
  dangerBtn: { background: '#d32f2f', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' },
  dangerBtnDisabled: { background: '#e5a5a5', cursor: 'not-allowed' },
  error: { color: '#d32f2f', background: '#fdecea', padding: '0.6rem 0.9rem', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.85rem' },
};
